import { HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { displaySnackBarMessage } from '@app/v2/shared/utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, createAction, createSelector, props, select, Store } from '@ngrx/store';
import { CommandRequestBuilderService } from '@servicesV2/command-request-builder.service';
import { TenantEvent } from '@servicesV2/command-request.service';
import { ApiEndPoints, DataService } from '@servicesV2/data.service';
import { LoggerService } from '@servicesV2/logger.service';
import { filter, map, Observable, pipe, switchMap } from 'rxjs';
import { AppState, ICommandResponse, IDomainMessage } from '../lemans-app.model';
import { UiStateActions } from '../ui-state';
import {
  emptyAction,
  getLoggedInUserRoleHierarchy,
  getTenantResourceHierarchy,
  setTenantId,
  UserDataFacade,
} from '../user-data';
import { RoleUpdateRequest, RoleUpdateResponse, UserAccessState, UserRoleHierarchy } from './domain';

const getUserRoleHierarchy = createAction(
  '[USER ACCESS] Get User Role Hierarchy',
  props<{ userId: string; tenantId: string }>(),
);
const setUserRoleHierarchy = createAction(
  '[USER ACCESS] Set User Role Hierarchy',
  props<{ userRoleHierarchy: UserRoleHierarchy }>(),
);

const saveUserRoles = createAction(
  '[USER ACCESS] Save User Roles',
  props<{ userId: string; roleUpdates: RoleUpdateRequest[] }>(),
);
// Emitted when a save has finished (success, partial failure or error), after the backend events were received.
// Nothing else refreshes userRoleHierarchy after a save, so the UI listens to this to reload the saved roles.
const saveUserRolesCompleted = createAction('[USER ACCESS] Save User Roles Completed');
const clearUserRoleHierarchy = createAction('[USER ACCESS] Clear User Role Hierarchy');

export const UserAccessActions = {
  getUserRoleHierarchy: getUserRoleHierarchy,
  setUserRoleHierarchy: setUserRoleHierarchy,
  saveUserRoles,
  saveUserRolesCompleted,
  clearUserRoleHierarchy: clearUserRoleHierarchy,
};

@Injectable({
  providedIn: 'root',
})
export class UserAccessFacade {
  private readonly store$ = inject(Store<AppState>);
  private readonly actions$ = inject(Actions);

  userRoleHierarchy$ = this.store$.pipe(userRoleHierarchy);

  userRolesSaved$ = this.actions$.pipe(ofType(UserAccessActions.saveUserRolesCompleted));

  getUserRoleHierarchy(userId: string, tenantId: string): void {
    this.store$.dispatch(getUserRoleHierarchy({ userId, tenantId }));
  }

  saveUserRoles(userId: string, roleUpdates: RoleUpdateRequest[]): void {
    this.store$.dispatch(saveUserRoles({ userId, roleUpdates: roleUpdates }));
  }

  clearUserRoleHierarchy(): void {
    this.store$.dispatch(clearUserRoleHierarchy());
  }
}

export const selectUserAccessFeature = (state: AppState): UserAccessState => state.userAccess;

const selectRoleHierarchy = createSelector(
  selectUserAccessFeature,
  (state: UserAccessState) => state.userRoleHierarchy,
);

// from https://ngrx.io/guide/store/selectors#solution-extracting-a-pipeable-operator
export const userRoleHierarchy = pipe(
  select(selectRoleHierarchy),
  filter((resources) => Boolean(resources.isReady)),
  map((resources) => resources.value),
);

@Injectable()
export class UserAccessEffects {
  private readonly actions$ = inject(Actions);
  private readonly dataService = inject(DataService);
  private readonly userDataFacade = inject(UserDataFacade);
  private readonly commandRequestBuilderService = inject(CommandRequestBuilderService);
  private readonly logger = inject(LoggerService);

  userID$ = this.userDataFacade.userID$;

  getUserRoleHierarchy$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(UserAccessActions.getUserRoleHierarchy),
      switchMap((payload) => this.dataService.getUserRoleHierarchy$(payload.tenantId, payload.userId)),
      map((userRoleHierarchy) => setUserRoleHierarchy({ userRoleHierarchy: userRoleHierarchy })),
    );
  });

  loadDeps$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(setTenantId),
      switchMap((payload) => {
        const tenantId = payload.value;
        return [getTenantResourceHierarchy({ tenantId }), getLoggedInUserRoleHierarchy({ tenantId })];
      }),
    );
  });

  // Create event predicate to wait for domain events
  saveRolesEventPredicateGenerator = (
    cmdResponse: ICommandResponse<RoleUpdateResponse>,
  ): ((domainMessage: IDomainMessage) => boolean) => {
    const roleUpdate = cmdResponse.response?.body;
    if (!roleUpdate) {
      this.logger.error('No role update response received, will not wait for any events');
      return () => true;
    }
    // accumulate the total number of events expected to be received
    const outcomes = Object.values(roleUpdate.outcome);
    const countOfEventsToWaitFor = outcomes.reduce((acc, item) => acc + (item?.eventsEmitted ?? 0), 0);
    let countOfEventsReceived = 0;
    return (domainMessage: IDomainMessage): boolean => {
      if (
        domainMessage.type === TenantEvent.ResourceAccessGranted ||
        domainMessage.type === TenantEvent.ResourceAccessRevoked
      ) {
        countOfEventsReceived = countOfEventsReceived + 1;
      }
      return countOfEventsReceived >= countOfEventsToWaitFor;
    };
  };

  saveUserRoles$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(UserAccessActions.saveUserRoles),
      switchMap((payload) => {
        const url = `${ApiEndPoints.Users}${payload.userId}/roles/batch`;
        const errorAction = displaySnackBarMessage(
          'None of your changes were saved. Please review and try saving again.',
          'Error',
        );

        const commandRequest = this.commandRequestBuilderService
          .new<RoleUpdateResponse>(url, 'POST')
          .withBody(payload.roleUpdates)
          .withWaitOn202Accepted(this.saveRolesEventPredicateGenerator)
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: errorAction,
            [HttpStatusCode.Conflict]: errorAction,
          });
        return commandRequest.invoke$();
      }),
      switchMap((response) => {
        if (response.error && response.errorActions) {
          return [...response.errorActions, saveUserRolesCompleted()];
        }

        const actions: Action[] = [];
        const roleUpdateResponse = response.response?.body;

        if (!roleUpdateResponse) {
          this.logger.error('No role update response received');
          return [emptyAction(), saveUserRolesCompleted()];
        }

        if (roleUpdateResponse.errorCount === 0) {
          actions.push(
            UiStateActions.displayMessage({
              payload: {
                message: 'Your data has been saved successfully.',
                type: 'Success',
              },
            }),
          );
        } else if (roleUpdateResponse.errorCount === Object.keys(roleUpdateResponse.outcome).length) {
          // all updates failed
          actions.push(
            UiStateActions.displayMessage({
              payload: {
                message: 'None of your changes were saved. Please review and try saving again.',
                type: 'Error',
              },
            }),
          );
        } else {
          // some updates failed
          actions.push(
            UiStateActions.displayMessage({
              payload: {
                message:
                  'Some changes were not saved. Please review the resources list to see which updates were applied.',
                type: 'Warning',
              },
            }),
          );
        }

        return [...actions, saveUserRolesCompleted()];
      }),
    );
  });
}
